import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { MonthlyIntelligenceRow } from '@/lib/destination-monthly-intelligence';

interface DbMonthlyIntelligenceRow {
  month: number;
  mean_deep_water_swell_height_m: number | null;
  mean_swell_period_s: number | null;
  groundswell_frequency_pct: number | null;
  favourable_wind_frequency_pct: number | null;
  prevailing_wind_direction: string | null;
  mean_water_temperature_c: number | null;
  mean_air_temperature_c: number | null;
  mean_daily_high_c: number | null;
  mean_daily_low_c: number | null;
  monthly_rainfall_mm: number | null;
  rain_days: number | null;
  surf_consistency_score_1_10: number | null;
  wind_quality_score_1_10: number | null;
  crowd_pressure_score_1_10: number | null;
  beginner_suitability_1_10: number | null;
  intermediate_suitability_1_10: number | null;
  advanced_suitability_1_10: number | null;
  expert_suitability_1_10: number | null;
  shoulder_season_value_1_10: number | null;
  comfort_score_1_10: number | null;
  overall_confidence: string | null;
  surfable_days_pct: number | null;
  surfable_days_pct_confidence: string | null;
  typical_breaking_wave_face_height_m: number | null;
  typical_breaking_wave_face_height_confidence: string | null;
}

const normaliseRow = (row: DbMonthlyIntelligenceRow): MonthlyIntelligenceRow => ({
  month: row.month,
  meanDeepWaterSwellHeightM: row.mean_deep_water_swell_height_m,
  meanSwellPeriodS: row.mean_swell_period_s,
  groundswellFrequencyPct: row.groundswell_frequency_pct,
  favourableWindFrequencyPct: row.favourable_wind_frequency_pct,
  prevailingWindDirection: row.prevailing_wind_direction,
  meanWaterTemperatureC: row.mean_water_temperature_c,
  meanAirTemperatureC: row.mean_air_temperature_c,
  meanDailyHighC: row.mean_daily_high_c,
  meanDailyLowC: row.mean_daily_low_c,
  monthlyRainfallMm: row.monthly_rainfall_mm,
  rainDays: row.rain_days,
  surfConsistencyScore: row.surf_consistency_score_1_10,
  windQualityScore: row.wind_quality_score_1_10,
  crowdPressureScore: row.crowd_pressure_score_1_10,
  beginnerSuitability: row.beginner_suitability_1_10,
  intermediateSuitability: row.intermediate_suitability_1_10,
  advancedSuitability: row.advanced_suitability_1_10,
  expertSuitability: row.expert_suitability_1_10,
  shoulderSeasonValue: row.shoulder_season_value_1_10,
  comfortScore: row.comfort_score_1_10,
  overallConfidence: row.overall_confidence,
  surfableDaysPct: row.surfable_days_pct,
  surfableDaysPctConfidence: row.surfable_days_pct_confidence,
  typicalBreakingWaveFaceHeightM: row.typical_breaking_wave_face_height_m,
  typicalBreakingWaveFaceHeightConfidence: row.typical_breaking_wave_face_height_confidence,
});

/** A destination with no monthly intelligence yet returns an empty array, not an
 * error -- the feature is additive and most destinations won't have this data
 * until the research pass scales beyond this first 3-destination pilot. */
export const useDestinationMonthlyIntelligence = (destinationId: string | undefined) => {
  return useQuery({
    queryKey: ['destination-monthly-intelligence', destinationId],
    enabled: !!destinationId,
    queryFn: async (): Promise<MonthlyIntelligenceRow[]> => {
      const { data, error } = await supabase
        .from('destination_monthly_intelligence')
        .select('*')
        .eq('destination_id', destinationId!)
        .order('month');
      if (error) throw error;
      return (data ?? []).map(normaliseRow);
    },
  });
};
